import React, { useState } from 'react';
import {
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
} from 'react-icons/io5';
import { VscEye, VscEyeClosed } from 'react-icons/vsc';

import {
  Actions,
  Column,
  Divider,
  ErrorText,
  Field,
  Fields,
  FormBody,
  Intro,
  OutlineButton,
  PrimaryButton,
  TextInput,
  Title,
  ToggleVisibility,
} from 'components/layout/Auth/AuthForm/styles';

import { passwordIsValid, passwordRules } from '../passwordRules';
import {
  RuleFail,
  RuleOk,
  RuleRow,
  RulesBlock,
  RulesList,
  RulesTitle,
} from '../styles';

type AlterarSenhaProps = {
  loading: boolean;
  erro: string | null;
  onSubmit: (senha: string) => void;
  onBack: () => void;
};

const AlterarSenha: React.FC<AlterarSenhaProps> = ({
  loading,
  erro,
  onSubmit,
  onBack,
}) => {
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [verConfirmacao, setVerConfirmacao] = useState(false);
  const [tocouConfirmacao, setTocouConfirmacao] = useState(false);

  const naoConferem =
    tocouConfirmacao && !!confirmacao && senha !== confirmacao;
  const podeEnviar =
    !loading &&
    passwordIsValid(senha) &&
    senha === confirmacao &&
    !!confirmacao;

  const olho = (visivel: boolean, alternar: () => void): React.ReactElement => (
    <ToggleVisibility
      type="button"
      onClick={alternar}
      aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
    >
      {visivel ? <VscEye /> : <VscEyeClosed />}
    </ToggleVisibility>
  );

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setTocouConfirmacao(true);
    if (podeEnviar) onSubmit(senha);
  };

  return (
    <Column gap={24}>
      <Intro>
        <Title>Alterar senha!</Title>
      </Intro>

      <form onSubmit={handleSubmit} noValidate>
        <FormBody>
          <Fields>
            <Field>
              <TextInput
                id="nova-senha"
                name="nova-senha"
                type={verSenha ? 'text' : 'password'}
                label="Insira sua nova senha"
                placeholder="Digite"
                required
                fullWidth
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                trailingIcon={olho(verSenha, () => setVerSenha((v) => !v))}
              />
            </Field>

            <Field>
              <TextInput
                id="confirmar-senha"
                name="confirmar-senha"
                type={verConfirmacao ? 'text' : 'password'}
                label="Confirmar senha"
                placeholder="Digite"
                required
                fullWidth
                error={naoConferem}
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                onBlur={() => setTocouConfirmacao(true)}
                trailingIcon={olho(verConfirmacao, () =>
                  setVerConfirmacao((v) => !v)
                )}
              />
              {naoConferem && <ErrorText>As senhas não conferem.</ErrorText>}
            </Field>
          </Fields>

          <Divider />

          <RulesBlock>
            <RulesTitle>Atenção! A senha cadastrada deverá ter:</RulesTitle>
            <RulesList>
              {passwordRules.map((rule) => {
                const ok = rule.test(senha);
                return (
                  <RuleRow key={rule.id}>
                    {rule.label}
                    {senha &&
                      (ok ? (
                        <RuleOk aria-label="atendido">
                          <IoCheckmarkCircleOutline aria-hidden="true" />
                        </RuleOk>
                      ) : (
                        <RuleFail aria-label="não atendido">
                          <IoCloseCircleOutline aria-hidden="true" />
                        </RuleFail>
                      ))}
                  </RuleRow>
                );
              })}
            </RulesList>
          </RulesBlock>

          <Divider />

          {erro && <ErrorText>{erro}</ErrorText>}

          <Actions>
            <OutlineButton
              type="button"
              variant="secondary"
              shape="pill"
              fullWidth
              onClick={onBack}
            >
              Voltar
            </OutlineButton>
            <PrimaryButton
              type="submit"
              shape="pill"
              fullWidth
              disabled={!podeEnviar}
            >
              {loading ? 'Alterando...' : 'Alterar'}
            </PrimaryButton>
          </Actions>
        </FormBody>
      </form>
    </Column>
  );
};

export default AlterarSenha;
